import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AddIcon } from "@/components/icons/action-icons";
import { EditAction, DeleteAction } from "@/components/table/actions";
import {
  useDeleteProjectKPIMutation,
  useGetAllProjectKPIsQuery,
} from "@/services/(company)/api.projects";
import { useMemo } from "react";
import { PROJECT_TAB_KEYS } from "@/routes/company/routes.projects.constants";
import { useProjectTabParams } from "../params";
import { ENUM_PROJECT_KPI_STATUSES } from "@/types/(company)/projects.interface";
import type { ISingleKPI } from "@/types/(company)/projects.interface";
import { Table, type ColumnDef } from "@/components/table";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useConfirm } from "@/context/Confirm";
import { PATHS } from "@/routes/paths";
import { usePermissions } from "@/hooks/usePermissions";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { useLocale } from "@/hooks/useLocale";

const buildColumns = (t: TFunction): ColumnDef<Record<string, unknown>>[] => [
  {
    key: "name",
    label: t("projects:kpi_name"),
  },
  {
    key: "description",
    label: t("common:labels.type"),
  },
  {
    key: "createdAt",
    label: t("projects:uploaded_on"),
  },
  {
    key: "status",
    label: t("projects:status"),
  },
  {
    key: "actions",
    label: t("projects:actions"),
  },
];

export const ProjectKPITable = ({ projectId }: { projectId: string }) => {
  const navigate = useNavigate();
  const { can } = usePermissions();
  // Search and page live in the URL, namespaced per tab — see useTableParams.
  const tp = useProjectTabParams(PROJECT_TAB_KEYS.Kpis);
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate } = useLocale();

  const { data: { data: kpis, meta } = {}, isFetching } =
    useGetAllProjectKPIsQuery({
      projectId,
      params: { page: tp.page, limit: 10, search: tp.committedSearch },
    });

  const kpiById = useMemo(() => new Map(kpis?.map((k) => [k.id, k])), [kpis]);

  const rows =
    kpis?.map((kpi) => ({
      id: kpi.id,
      name: (
        <Link
          to={PATHS.COMPANY.COMPANY_PROJECTS_PATHS.EDIT_KPI(projectId, kpi.id)}
        >
          {kpi.name}
        </Link>
      ),
      description: <p className="max-w-md truncate">{kpi.description}</p>,
      createdAt: formatDate(new Date(kpi.createdAt), "dd MMM yyyy"),
      status: <KPIStatus status={kpi.status} />,
      actions: <RowActions projectId={projectId} kpi={kpi} />,
    })) ?? [];

  return (
    <Table
      data={rows}
      loading={isFetching}
      searchText={tp.search}
      columns={buildColumns(t)}
      noPadding
      pagination={{
        totalPages: meta?.totalPages ?? 1,
        currentPage: tp.page,
        onPageChange: tp.setPage,
      }}
      onSearchChange={tp.setSearch}
      searchPlaceholder={t("projects:search_by_team_name")}
      actions={[
        ...(can(PLATFORM_SECTION_ENUM.PROJECTS, "create")
          ? [
              {
                type: "link" as const,
                key: "add",
                label: t("projects:add_new"),
                icon: <AddIcon className="size-4" />,
                variant: "primary" as const,
                href: PATHS.COMPANY.COMPANY_PROJECTS_PATHS.ADD_KPI(projectId),
              },
            ]
          : []),
      ]}
      onRowClick={(row) => {
        const kpi = kpiById.get(row.id as number);
        if (kpi)
          navigate(
            PATHS.COMPANY.COMPANY_PROJECTS_PATHS.VIEW_KPI(projectId, kpi.id),
            { state: { kpi } },
          );
      }}
    />
  );
};

const RowActions = ({
  projectId,
  kpi,
}: {
  projectId: string;
  kpi: ISingleKPI;
}) => {
  const { t } = useTranslation(["projects", "common"]);
  const confirm = useConfirm();
  const { can } = usePermissions();
  const [deleteKPI, { isLoading: isDeleting }] = useDeleteProjectKPIMutation();

  const handleDeleteKPI = async (kpiId: string) => {
    confirm({
      title: t("projects:delete_kpi"),
      msg: t("projects:are_you_sure_you_want_to_delete_this_kpi"),
      onConfirm: async () => {
        await deleteKPI({ projectId, kpiId })
          .unwrap()
          .then((res) => toast.success(res.message))
          .catch((e) => toast.error(e?.data?.message ?? e?.message));
      },
    });
  };

  return (
    <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
      {can(PLATFORM_SECTION_ENUM.PROJECTS, "edit") && (
        <Link
          to={PATHS.COMPANY.COMPANY_PROJECTS_PATHS.EDIT_KPI(projectId, kpi.id)}
        >
          <EditAction tooltip={t("projects:edit")} />
        </Link>
      )}
      <DeleteAction
        tooltip={t("projects:delete_kpi")}
        disabled={isDeleting || !can(PLATFORM_SECTION_ENUM.PROJECTS, "delete")}
        onClick={() => handleDeleteKPI(kpi.id.toString())}
      />
    </div>
  );
};

const KPIStatus = ({ status }: { status: ENUM_PROJECT_KPI_STATUSES }) => {
  const { t } = useTranslation("projects");
  const colors = {
    [ENUM_PROJECT_KPI_STATUSES.COMPLETED]: "text-green-500",
    [ENUM_PROJECT_KPI_STATUSES.IN_PROGRESS]: "text-purple-500",
    [ENUM_PROJECT_KPI_STATUSES.TODO]: "text-pink-500",
  };
  return (
    <p className={`capitalize font-semibold ${colors[status]}`}>{t(status)}</p>
  );
};
